import { useState } from "react";
import { Phone, Mail, Instagram, Linkedin, Facebook, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { SITE, PRODUCT_CATEGORIES } from "@/config/site";

const initial = { name: "", company: "", country: "", email: "", phone: "", category: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    const subject = `Trade Enquiry from ${form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Company: ${form.company || "-"}`,
      `Country: ${form.country || "-"}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone || "-"}`,
      `Product Category: ${form.category || "-"}`,
      "",
      "Message:",
      form.message,
    ].join("\n");
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Opening your email app with the enquiry pre-filled.");
  };

  const field = "mt-2 h-12 rounded-2xl border-navy/15 bg-white text-navy focus-visible:ring-sky";

  return (
    <div data-testid="contact-page">
      <Toaster position="top-center" richColors />

      {/* HEADER */}
      <section className="relative bg-navy px-6 pb-20 pt-40 text-white md:px-12 md:pb-24 md:pt-52 lg:px-16" data-testid="contact-hero">
        <div className="mx-auto max-w-[1600px]">
          <Reveal><p className="mb-6 font-display text-xs font-semibold uppercase tracking-[0.34em] text-sky md:text-sm">Send Your Requirement</p></Reveal>
          <h1 className="font-display text-5xl font-black leading-[0.9] tracking-tighter md:text-8xl">
            <MaskReveal lines={["Start a Trade", "Enquiry"]} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-lg font-light text-white/70">
              Tell us what you need — product, sizes, GSM and volumes. Submitting opens your own email app with everything pre-filled, ready to send.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28 lg:px-16" data-testid="contact-body">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.4fr] lg:gap-24">
          {/* CONTACT INFO */}
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy md:text-4xl">Let's talk trade.</h2>
            <p className="mt-4 text-navy/60">Reach us directly through any channel below.</p>
            <div className="mt-10 space-y-2">
              <a href={SITE.phoneHref} data-testid="contact-phone" className="group flex items-center gap-5 border-b border-black/10 py-5 transition-colors hover:text-sky">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white transition-colors group-hover:bg-sky"><Phone className="h-5 w-5" /></span>
                <span><span className="block text-xs uppercase tracking-[0.2em] text-navy/40">Phone</span><span className="font-display text-lg font-semibold text-navy">{SITE.phone}</span></span>
              </a>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="group flex items-center gap-5 border-b border-black/10 py-5 transition-colors hover:text-sky">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white transition-colors group-hover:bg-sky"><Mail className="h-5 w-5" /></span>
                <span><span className="block text-xs uppercase tracking-[0.2em] text-navy/40">Email</span><span className="font-display text-base font-semibold text-navy break-all">{SITE.email}</span></span>
              </a>
              <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="group flex items-center gap-5 border-b border-black/10 py-5 transition-colors hover:text-sky">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white transition-colors group-hover:bg-sky"><Instagram className="h-5 w-5" /></span>
                <span><span className="block text-xs uppercase tracking-[0.2em] text-navy/40">Instagram</span><span className="font-display text-lg font-semibold text-navy">@bwtuniverse_</span></span>
              </a>
              <a href={SITE.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin" className="group flex items-center gap-5 border-b border-black/10 py-5 transition-colors hover:text-sky">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white transition-colors group-hover:bg-sky"><Linkedin className="h-5 w-5" /></span>
                <span><span className="block text-xs uppercase tracking-[0.2em] text-navy/40">LinkedIn</span><span className="font-display text-lg font-semibold text-navy">Brahmandnayak World Trade</span></span>
              </a>
              <a href={SITE.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" className="group flex items-center gap-5 border-b border-black/10 py-5 transition-colors hover:text-sky">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white transition-colors group-hover:bg-sky"><Facebook className="h-5 w-5" /></span>
                <span><span className="block text-xs uppercase tracking-[0.2em] text-navy/40">Facebook</span><span className="font-display text-lg font-semibold text-navy">BWT Universe</span></span>
              </a>
              <a href={SITE.location} target="_blank" rel="noreferrer" data-testid="contact-location" className="group flex items-center gap-5 py-5 transition-colors hover:text-sky">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white transition-colors group-hover:bg-sky"><MapPin className="h-5 w-5" /></span>
                <span><span className="block text-xs uppercase tracking-[0.2em] text-navy/40">Location</span><span className="font-display text-lg font-semibold text-navy">India · View on map</span></span>
              </a>
            </div>

            {/* MINI MAP PANEL */}
            <div className="group relative mt-6 overflow-hidden rounded-3xl border border-black/10" data-testid="contact-map">
              <iframe
                title="Bhargavrushi Textiles location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3801.7980558838617!2d75.94956107549729!3d17.65972109490201!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc5db003c0f44bf%3A0x5d2d16288158d48b!2sBHARGAVRUSHI%20TEXTILES!5e0!3m2!1sen!2sin!4v1787078565832!5m2!1sen!2sin"
                className="h-64 w-full md:h-72"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </Reveal>

          {/* FORM */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="enquiry-form" className="rounded-2xl border border-black/10 bg-white p-8 md:p-12">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="font-display text-sm font-semibold text-navy">Name *</Label>
                  <Input id="name" data-testid="form-name" value={form.name} onChange={set("name")} placeholder="Your full name" className={field} />
                </div>
                <div>
                  <Label htmlFor="company" className="font-display text-sm font-semibold text-navy">Company</Label>
                  <Input id="company" data-testid="form-company" value={form.company} onChange={set("company")} placeholder="Company name" className={field} />
                </div>
                <div>
                  <Label htmlFor="country" className="font-display text-sm font-semibold text-navy">Country</Label>
                  <Input id="country" data-testid="form-country" value={form.country} onChange={set("country")} placeholder="Country" className={field} />
                </div>
                <div>
                  <Label htmlFor="email" className="font-display text-sm font-semibold text-navy">Email *</Label>
                  <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={field} />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-display text-sm font-semibold text-navy">Phone</Label>
                  <Input id="phone" data-testid="form-phone" value={form.phone} onChange={set("phone")} placeholder="+00 000 000 0000" className={field} />
                </div>
                <div>
                  <Label className="font-display text-sm font-semibold text-navy">Product Category</Label>
                  <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger data-testid="form-category" className={field}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_CATEGORIES.map((p) => (
                        <SelectItem key={p.key} value={p.name} data-testid={`category-option-${p.key}`}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6">
                <Label htmlFor="message" className="font-display text-sm font-semibold text-navy">Message *</Label>
                <Textarea id="message" data-testid="form-message" value={form.message} onChange={set("message")} rows={5} placeholder="Tell us about your requirement — sizes, GSM, quantity, target price..." className="mt-2 rounded-2xl border-navy/15 bg-white text-navy focus-visible:ring-sky" />
              </div>
              <button type="submit" data-testid="form-submit" className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-sky sm:w-auto">
                Send Your Requirement
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
